"use client";

import { Typography } from "@drpcrd/shared/ui";
import { cn } from "@drpcrd/utils/shared";
import type { ComponentType, ReactNode } from "react";

export type NavigationItem = {
  name: string;
  href: string;
  icon: ComponentType<{ className?: string; strokeWidth?: number }>;
};

type LinkComponent = ComponentType<{
  href: string;
  className?: string;
  children: ReactNode;
}>;

type BottomNavigationProps = {
  /** Link component from the routing library (e.g., Next.js Link, React Router Link) */
  Link: LinkComponent;
  /** Current pathname to determine active navigation item */
  pathname: string;
  /** The items to display in the navigation */
  items: NavigationItem[];
};

/**
 * Framework-agnostic bottom navigation bar providing quick access to main sections of the Creator Dashboard.
 * Features visual feedback for the active page and smooth transitions between navigation items.
 */
export const BottomNavigation = ({
  Link,
  pathname,
  items,
}: BottomNavigationProps) => (
  <nav
    className="border-border-primary bg-bg-primary fixed bottom-0 left-0 mt-auto w-full border-t"
    aria-label="Bottom Navigation"
  >
    <div className="mx-auto flex h-fit max-h-[90px] w-full max-w-54 items-center justify-between">
      {items.map((item) => {
        const isActive = pathname.startsWith(item.href);
        const Icon = item.icon;

        return (
          <Link
            key={item.name}
            href={item.href}
            className="flex w-full flex-col items-center pt-1.5 pb-3 transition-colors"
          >
            <div
              className={cn(
                "rounded-2xl px-2.5 py-0.75 transition-colors",
                isActive
                  ? "bg-bg-status-primary"
                  : "hover:bg-bg-status-primary bg-transparent",
              )}
            >
              <Icon
                className={cn(
                  "h-2.5 w-2.5 transition-colors",
                  isActive ? "text-icon-action" : "text-icon-secondary",
                )}
              />
            </div>

            <Typography
              size="base"
              weight="semibold"
              color={isActive ? "black" : "neutral"}
              className="leading-2.75"
            >
              {item.name}
            </Typography>
          </Link>
        );
      })}
    </div>
  </nav>
);
